import type {
  RemoveCarFilesQueries,
  UpdateCarDocumentQueries,
} from 'store/endpoints/types';
import type {
  Car,
  CarRequest,
  GetCarsParams,
  SearchCarsParam,
} from 'store/slices/cars/types';
import type { Dayjs } from 'dayjs';
import type { CAR_FILTERS_TYPE } from 'components/Admin/organisms/CarAvailability/molecules/CarAvailabilitiesHeader/constants';
import type { CarStatus } from 'store/slices/cars/types';
import { endpoints } from 'store/endpoints';
import { axiosInstance } from './axiosInstance';

export class CarsService {
  static async updateCarMedia(
    images: Array<File>,
    carId: string,
    onProgressChange: (progress: number) => void,
  ) {
    try {
      const formData = new FormData();
      images.forEach((item) => formData.append('images', item));
      const response = await axiosInstance.put(
        endpoints.cars.updateCarMedia(carId),
        formData,
        {
          onUploadProgress: (progressEvent) => {
            const { loaded, total } = progressEvent;
            if (!total) {
              return;
            }
            const progress = Math.round((loaded * 100) / total);
            if (progress === 100) {
              setTimeout(() => {
                onProgressChange(0);
              }, 3000);
            }
            setTimeout(() => {
              onProgressChange(progress);
            }, 1000);
          },
        },
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async updateCarInitialPhoto(file: File, carId: string) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axiosInstance.patch(
        endpoints.cars.updateCarInitialPhoto(carId),
        formData,
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async removeCarInitialPhoto(imageId: string) {
    try {
      const response = await axiosInstance.delete(
        endpoints.cars.deleteCarInitialPhoto(imageId),
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async updateCarFile({ file, carId }: { file: File; carId: string }) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axiosInstance.post<Car>(
        endpoints.cars.updateCarFile(carId),
        formData,
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }

  static async updateCoverImage({
    file,
    carId,
  }: {
    file: File;
    carId: string;
  }) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axiosInstance.put<Car>(
        endpoints.cars.updateCarCoverImage(carId),
        formData,
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async updateCarDocument(
    params: UpdateCarDocumentQueries & { id: string },
  ) {
    try {
      const formData = new FormData();
      const { file, ...rest } = params;
      formData.append('file', file);

      const response = await axiosInstance.patch<Car>(
        endpoints.cars.updateCarDocument(rest),
        formData,
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async removeCarFile(params: RemoveCarFilesQueries) {
    try {
      const response = await axiosInstance.delete<Car>(
        endpoints.cars.removeCarFiles(params),
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async duplicateCar(id: string) {
    try {
      const response = await axiosInstance.post(
        endpoints.cars.duplicateCar(id),
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async updateCars(selectedCars: Array<string>, status: CarStatus) {
    try {
      const response = await axiosInstance.patch<Array<Car>>(
        endpoints.cars.updateCarsStatuses(selectedCars.join(',')),
        {
          status,
        },
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }

  static async searchCars(params: Partial<SearchCarsParam>) {
    try {
      const response = await axiosInstance.get(endpoints.cars.search, {
        params,
      });
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async getCars(params?: Partial<GetCarsParams>) {
    try {
      const response = await axiosInstance.get<{
        rows: Array<Car>;
        count: number;
      }>(endpoints.cars.general, {
        params,
      });
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }

  static async getCarsByAvailability(
    availabilityType: CAR_FILTERS_TYPE,
    dates: Array<Dayjs>,
  ) {
    try {
      const response = await axiosInstance.get<Array<Car>>(
        endpoints.cars.getCarsByAvailability,
        {
          params: {
            availabilityType,
            dates,
          },
        },
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }

  static async editCar(body: Partial<Car>, id: string) {
    try {
      const response = await axiosInstance.patch<Car>(
        endpoints.cars.opWithId(id),
        body,
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }

  static async getCarById(id: string) {
    try {
      const response = await axiosInstance.get<Car>(
        endpoints.cars.opWithId(id),
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async removeCars(selectedCars: Array<string>) {
    try {
      await axiosInstance.delete(
        endpoints.cars.deleteSelectedCars(selectedCars),
      );
    } catch (err: any) {
      throw new Error(err);
    }
  }

  static async createNewCar(body: CarRequest) {
    try {
      const formData = new FormData();
      const {
        images,
        videoGuide,
        registrationDate,
        insuranceDoc,
        registrationDoc,
        insuranceInformation,
        additionalFiles,
        initialCarPhotos,
        generalThumbnail,
        ...rest
      } = body;
      Array.from(images || []).forEach((item) => {
        formData.append('images', item.file);
      });

      if (registrationDoc) {
        formData.append('registrationDoc', registrationDoc);
      }
      if (insuranceDoc) {
        formData.append('insuranceDoc', insuranceDoc);
      }

      if (insuranceInformation) {
        formData.append('insuranceInformation', insuranceInformation);
      }
      (additionalFiles || []).forEach((item) => {
        formData.append('additionalFiles', item.file);
      });

      (initialCarPhotos || []).forEach((item) => {
        formData.append('initialCarPhotos', item.file);
      });

      formData.append('videoGuide', videoGuide as File);
      if (generalThumbnail instanceof File) {
        formData.append('generalThumbnail', generalThumbnail);
      }
      formData.append('body', JSON.stringify(rest));

      const response = await axiosInstance.post(
        endpoints.cars.general,
        formData,
      );

      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }

  static async addAvailabilityDates(body: Array<string>, carId: string) {
    try {
      const response = await axiosInstance.patch(
        endpoints.cars.addAvailabilityDates(carId),
        body,
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async removeAvailableDate(body: Array<string>, carId: string) {
    try {
      const response = await axiosInstance.patch(
        endpoints.cars.removeAvailableDate(carId),
        body,
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
}
